import urlMapping from '@next-app/config/urlMapping';
import {
  containsEmptyValues,
  isNotEmptyObject,
  isNullOrUndefined,
  refetch,
} from '@next-app/utils/common-utils';
import { makeFetchCall } from '@next-app/utils/fetch-data';
import {
  setLoadCheckoutAdditionalMsg,
  setLoadCheckoutDetails,
} from '@next-app/lib/features/checkout/CheckoutSlice';
import {
  ApplyBillingReqBody,
  CheckoutServerErrorResponse,
  CheckoutStep,
  CommerceItemInfo,
  FormException,
  IApplyGiftCardResponse,
  IApplyPromoCodeRequest,
  ISubmitOrderRequest,
  ISubmitOrderResponse,
  ITaxExemptRequest,
  ShippingAddressResponse,
} from '@next-app/lib/features/checkout/Checkout.interface';
import { SaveDeliveryAddressRequest } from '@next-app/interface/Checkout/SaveDeliveryAddress';
import { IShippingMethodRequest } from '@next-app/interface/Checkout/ShippingSection';
import { IApplyGiftCardRequest } from '@next-app/interface/Checkout/ApplyGiftCard';
import { ISaveGuestEmailDetailsRequest } from '@next-app/interface/Checkout/SaveGuestEmailDetails';
import {
  PAYMENT,
  pointToDollarMap,
} from '@next-app/constants/CheckoutConstants';
import { IConvertPointsToBucksRequest } from '@next-app/interface/Checkout/LakeshoreBucksSection';
import {
  SessionInfoAvailableBucks,
  SessionInformationRewardsCustomer,
} from '@next-app/interface/SessionInformation';
import { BillingAddressFormFields } from '@next-app/components/Checkout/BillingAddressForm/BillingAddressFormFields';
import { ShoppingCartData } from '@next-app/lib/features/ShoppingCart/ShoppingCart.interface';
import { DynamicDTMEvent } from './dtm-event';
import { SessionData } from '@next-app/interface/Common';

/**
 * fetchCheckoutDetails
 * @param param0
 */
export const fetchCheckoutDetails = async ({
  dynSessConf,
  dispatch,
  isFurniture = false,
}: {
  dynSessConf: Function;
  dispatch: Function;
  isFurniture?: boolean;
}) => {
  const body = {
    sortProperties: '+lastname',
    howMany: -1,
    start: 0,
    isCheckoutAddresses: true,
    checkoutPageLoad: true,
    _dynSessConf: dynSessConf,
  };

  const reqOptions = {
    endpoint: urlMapping.loadCheckoutDetails,
    body,
    options: { method: 'POST' },
    headers: { 'Is-Furniture': isFurniture ? 'true' : 'false' },
  };

  try {
    let data = await makeFetchCall(reqOptions);

    if (data?.stringSessionConfirmationNumber) {
      data = refetch({ response: data, request: reqOptions, dispatch });
    }

    const emptyCart =
      data?.checkoutResponse?.emptyCart === true ||
      data?.error?.redirectUrl?.indexOf('shopping-cart') > -1;
    if (emptyCart) {
      window.location.href = '/shopping-cart/';
      return;
    } else if (data?.checkoutResponse) {
      dispatch(setLoadCheckoutDetails(data?.checkoutResponse));
      if (data?.message) {
        dispatch(setLoadCheckoutAdditionalMsg(data?.message));
      }
    }
  } catch (error: any) {
    console.error('Error in fetchCheckoutDetails', error, body);
    return error;
  }
};

/**
 * isCurrentStepValid
 * @param data
 * @param currentStep
 * @returns
 */
export const isCurrentStepValid = (
  data: CheckoutStep[],
  currentStep: string,
) => {
  for (let i = 0; i < data?.length; i++) {
    if (data[i]?.key === currentStep) {
      // Check if all previous elements have currentStep is true
      return data?.slice(0, i).every((item) => item.readyForSubmit === true);
    }
  }
  return false; // Return false if currentStep is not found
};

/**
 * Update Shipping Method
 * @param body
 * @returns response object
 */
export const updateShippingMethodDetails = async (
  body: IShippingMethodRequest,
  isFurniture = false,
) => {
  try {
    const shippingMethodResponse = await makeFetchCall({
      endpoint: urlMapping.shippingMethod,
      body,
      options: { method: 'POST' },
      headers: { 'Is-Furniture': isFurniture ? 'true' : 'false' },
    });

    return shippingMethodResponse;
  } catch (error: any) {
    console.error('Error in updateShippingMethodDetails', error, body);
    return error;
  }
};

/**
 * saveDeliveryAddress
 * @param reqbody
 * @returns
 */
export const saveDeliveryAddress = async (
  reqbody: Partial<SaveDeliveryAddressRequest>,
  isFurniture = false,
): Promise<ShippingAddressResponse & CheckoutServerErrorResponse> => {
  try {
    const response = await makeFetchCall({
      endpoint: `${urlMapping.shippingAddressUrl}`,
      options: {
        method: 'POST',
      },
      body: reqbody,
      headers: { 'Is-Furniture': isFurniture ? 'true' : 'false' },
    });
    return response;
  } catch (error: any) {
    console.error('Error Shipping address', error, reqbody);
    return error;
  }
};

/**
 * handleServerErrors
 * @param serverSideErrors
 */
export const handleCheckoutServerErrors = (
  serverSideErrors: FormException[],
  setFormErrors: Function,
): void => {
  let formErrors: Record<string, string> = {};
  serverSideErrors.forEach(
    ({ propertyPath, localizedMessage }: FormException) => {
      formErrors[propertyPath] = localizedMessage;
    },
  );
  setFormErrors((prevState: any) => ({ ...prevState, ...formErrors }));
};

/**
 * validateFields
 * Validates a group of fields
 * @param fields
 * @returns
 */
export const validateFields = (
  fields: Record<string, any>,
  validateField: Function,
  setFormErrors: Function,
) => {
  let errorMessages: Record<string, string> = {};
  Object.entries(fields).forEach(([name, value]) => {
    errorMessages[name] = validateField(name, value);
  });
  setFormErrors((prev: any) => ({ ...prev, ...errorMessages }));
  return containsEmptyValues(errorMessages);
};

/**
 * applyGiftCardReq
 * @param body Partial<IApplyGiftCardRequest>
 * @returns Promise<IApplyGiftCardResponse & CheckoutServerErrorResponse>
 */
export const applyGiftCardReq = async (
  body: Partial<IApplyGiftCardRequest>,
  isFurniture = false,
): Promise<IApplyGiftCardResponse & CheckoutServerErrorResponse> => {
  try {
    const shippingMethodResponse = await makeFetchCall({
      endpoint: urlMapping.applyGiftCardUrl,
      body,
      options: { method: 'POST' },
      headers: { 'Is-Furniture': isFurniture ? 'true' : 'false' },
    });

    return shippingMethodResponse;
  } catch (error: any) {
    console.error('Error in applyGiftCardUrl', error, body);
    return error;
  }
};

/**
 * removeGiftCardReq
 * @param body Partial<IApplyGiftCardRequest>
 * @returns Promise<IApplyGiftCardResponse & CheckoutServerErrorResponse>
 */
export const removeGiftCardReq = async (
  body: {
    gcNumber: string[];
    _dynSessConf: string;
  },
  isFurniture = false,
): Promise<IApplyGiftCardResponse & CheckoutServerErrorResponse> => {
  try {
    const shippingMethodResponse = await makeFetchCall({
      endpoint: urlMapping.removeGiftCardUrl,
      body,
      options: { method: 'POST' },
      headers: { 'Is-Furniture': isFurniture ? 'true' : 'false' },
    });

    return shippingMethodResponse;
  } catch (error: any) {
    console.error('Error in applyGiftCardUrl', error, body);
    return error;
  }
};

/**
 * saveGuestEmailDetails
 * @param reqBody
 * @returns
 */
export const saveGuestEmailDetails = async (
  reqBody: Partial<ISaveGuestEmailDetailsRequest>,
  isFurniture = false,
): Promise<ShippingAddressResponse & CheckoutServerErrorResponse> => {
  try {
    const response = await makeFetchCall({
      endpoint: `${urlMapping.saveGuestEmailDetails}`,
      options: {
        method: 'POST',
      },
      body: reqBody,
      headers: { 'Is-Furniture': isFurniture ? 'true' : 'false' },
    });
    return response;
  } catch (error: any) {
    console.error('Error saveGuestEmailDetails', error, reqBody);
    return error;
  }
};

/**
 * loginProfileCheck
 * @param reqBody
 */
export const loginProfileCheck = async (
  reqBody: {
    login: string;
    userType: string;
    _dynSessConf: string;
  },
  isFurniture = false,
) => {
  try {
    let responseData = await makeFetchCall({
      endpoint: urlMapping.loginProfileCheck,
      body: reqBody,
      options: { method: 'POST' },
      metadata: {
        statusCode: true,
      },
      headers: { 'Is-Furniture': isFurniture ? 'true' : 'false' },
    });
    if (
      responseData?.status === 'success' &&
      responseData?.statusCode === 200
    ) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error('Error in loginProfileCheck', error);
    return false;
  }
};

export const applyBilling = async (
  body: ApplyBillingReqBody,
  isFurniture = false,
) => {
  const applyBillingResp = await makeFetchCall({
    endpoint: urlMapping.applyBillingUrl,
    body,
    options: { method: 'POST' },
    headers: { 'Is-Furniture': isFurniture ? 'true' : 'false' },
  });

  return applyBillingResp;
};

/**
 * maskGiftCardNumber
 * @param gcNum
 * @returns maskedGCNumber in ••••xxxx format
 */
export const maskGiftCardNumber = (gcNum: string): string => {
  if (isNullOrUndefined(gcNum)) {
    return '••••';
  }
  return `••••${gcNum?.slice(-4)}`;
};

/**
 * convertPointsToDollars
 * @param reqBody
 */
export const convertPointsToDollars = (points: number) => {
  if (points < 500) return 0; // Less than 500 points = $0

  let totalDollars = 0;
  for (const rate of pointToDollarMap) {
    while (points >= rate.points) {
      totalDollars += rate.dollars;
      points -= rate.points;
    }
  }

  return formatNumberWithCommaAndDecimal(totalDollars);
};

/**
 * getTotalLakeshoreBucks
 * @param lakeshoreBucks
 * @param availableBucks
 */
export const getTotalLakeshoreBucks = (
  lakeshoreBucks: string[],
  availableBucks: SessionInfoAvailableBucks[],
) => {
  const total = availableBucks
    .filter((buck) => lakeshoreBucks?.includes(buck.buckId)) // Match buckId
    .reduce((sum, buck) => sum + buck.buckAmount, 0); // Sum buckAmount

  return formatNumberWithCommaAndDecimal(total);
};

/**
 * getAvailableBucks
 * @param availableBucks
 */
export const getAvailableBucks = (
  availableBucks: SessionInfoAvailableBucks[],
) => {
  const total = availableBucks.reduce((sum, buck) => sum + buck.buckAmount, 0); // Sum buckAmount

  return formatNumberWithCommaAndDecimal(total);
};

export function formatNumberWithComma(num: number) {
  return Number(num).toLocaleString('en-US');
}

export function formatNumberWithCommaAndDecimal(num: number | string) {
  return Number(num).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * convertPointsToBucks
 * @param reqBody
 * @returns
 */
export const convertPointsToBucks = async (
  reqBody: Partial<IConvertPointsToBucksRequest>,
  isFurniture = false,
) => {
  try {
    const response = await makeFetchCall({
      endpoint: `${urlMapping.convertPointsToBucksUrl}`,
      options: {
        method: 'POST',
      },
      body: reqBody,
      headers: { 'Is-Furniture': isFurniture ? 'true' : 'false' },
    });
    return response;
  } catch (error: any) {
    console.error('Error in convertPointsToBucks', error);
    return error;
  }
};

/**
 * convertPointsToBucks
 * @param reqBody
 * @returns
 */
export const applyLakeshoreBucks = async (
  reqBody: Partial<IConvertPointsToBucksRequest>,
  isFurniture = false,
) => {
  try {
    const response = await makeFetchCall({
      endpoint: `${urlMapping.applyBucksUrl}`,
      options: {
        method: 'POST',
      },
      body: reqBody,
      headers: { 'Is-Furniture': isFurniture ? 'true' : 'false' },
    });
    return response;
  } catch (error: any) {
    console.error('Error in apply bucks', error);
    return error;
  }
};

/**
 * findNewlyAddedBucks
 * @param newAvailableBucks
 * @param oldAvailableBucks
 * @return boolean
 */
export function findNewlyAddedBucks(
  newAvailableBucks: SessionInfoAvailableBucks[] | null,
  oldAvailableBucks: SessionInfoAvailableBucks[] | null,
) {
  const oldIds = new Set(oldAvailableBucks?.map((item) => item.buckId));
  const response = newAvailableBucks?.find((item) => !oldIds.has(item.buckId));
  return response?.buckId;
}

export function compareCommonFields(
  loqateAddr: Record<string, any>,
  formAddr: Record<string, any>,
) {
  const commonKeys = Object.keys(loqateAddr).filter((key) =>
    Object.keys(formAddr).includes(key),
  );

  for (let key of commonKeys) {
    if (loqateAddr[key] !== formAddr[key]) {
      return false;
    }
  }

  return true;
}

/**
 * applyTaxExempt
 * @param body
 * @returns Promise
 */
export const applyTaxExempt = async (
  body: ITaxExemptRequest,
  isFurniture = false,
): Promise<ShippingAddressResponse & CheckoutServerErrorResponse> => {
  try {
    const response = await makeFetchCall({
      endpoint: urlMapping.taxExemptUrl,
      body,
      options: { method: 'POST' },
      headers: { 'Is-Furniture': isFurniture ? 'true' : 'false' },
    });

    return response;
  } catch (error: any) {
    console.error('Error in applying tax exempt', error, body);
    return error;
  }
};

/**
 * Apply Promo Code
 * @param reqBody
 * @returns
 */
export const applyPromoCode = async (
  reqBody: Partial<IApplyPromoCodeRequest>,
  isFurniture = false,
  dispatch: Function,
): Promise<ShippingAddressResponse & CheckoutServerErrorResponse> => {
  try {
    const request = {
      endpoint: `${urlMapping.claimCoupon}`,
      options: {
        method: 'POST',
      },
      body: reqBody,
      headers: { 'Is-Furniture': isFurniture ? 'true' : 'false' },
    };
    let response = await makeFetchCall(request);
    if (response?.stringSessionConfirmationNumber) {
      response = await refetch({
        response,
        request,
        dispatch,
      });
    }
    return response;
  } catch (error: any) {
    console.error('Error Claim Coupon', error, reqBody);
    return error;
  }
};

/**
 * Remove Promo Code
 * @param reqBody
 * @returns
 */
export const removePromoCode = async (
  reqBody: Partial<IApplyPromoCodeRequest>,
  isFurniture = false,
  dispatch: Function,
): Promise<
  (ShippingAddressResponse | ShoppingCartData | SessionData) &
    CheckoutServerErrorResponse
> => {
  try {
    const request = {
      endpoint: `${urlMapping.revokeCoupon}`,
      options: {
        method: 'POST',
      },
      body: reqBody,
      headers: { 'Is-Furniture': isFurniture ? 'true' : 'false' },
    };

    let response = await makeFetchCall(request);

    if (response?.stringSessionConfirmationNumber) {
      response = await refetch({
        response,
        request,
        dispatch,
      });
    }
    return response;
  } catch (error: any) {
    console.error('Error Claim Coupon', error, reqBody);
    return error;
  }
};

/**
 * submitOrderReq
 * @param body Partial<ISubmitOrderRequest>
 * @returns Promise<ISubmitOrderResponse>
 */
export const submitOrderReq = async (
  body: ISubmitOrderRequest,
  isFurniture = false,
): Promise<ISubmitOrderResponse> => {
  try {
    const submitOrderResponse = await makeFetchCall({
      endpoint: urlMapping.submitOrder,
      body,
      options: { method: 'POST' },
      headers: { 'Is-Furniture': isFurniture ? 'true' : 'false' },
    });

    return submitOrderResponse;
  } catch (error: any) {
    console.error('Error in submit order', error, body);
    return error;
  }
};

export const removeDollar = (displayTotal: string) => {
  if (!displayTotal.includes('$')) return true;
  return Number(displayTotal.replace('$', ''));
};

export const validateFormDetails = ({
  formError,
  formData,
  billingAddressForm,
  billingAddressFormError,
  isSameAsDelivery = false,
}: {
  formError: any;
  formData: any;
  billingAddressForm?: any;
  billingAddressFormError?: any;
  isSameAsDelivery: boolean;
}) => {
  const checkValue = (data: any) =>
    Object.values(data).every(
      (value) => value !== '' && value !== null && value !== undefined,
    );
  const hasFormError = (data: any) => {
    return (
      Object.keys(data).length === 0 ||
      Object.values(data).every((error) => error === '')
    );
  };
  const requiredBillingFields = [
    BillingAddressFormFields.FIRST_NAME,
    BillingAddressFormFields.LAST_NAME,
    BillingAddressFormFields.COUNTRY,
    BillingAddressFormFields.CITY,
    BillingAddressFormFields.STATE,
    BillingAddressFormFields.POSTAL_CODE,
    BillingAddressFormFields.ADDRESS_ONE,
    BillingAddressFormFields.PHONE_NO,
  ];

  if (isSameAsDelivery) {
    const isValidFormData = checkValue(formData);
    const hasNoFormError = hasFormError(formError);
    return isValidFormData && hasNoFormError;
  } else if (isNotEmptyObject(billingAddressForm)) {
    const isValidFormData = checkValue(formData);
    const hasNoFormError = hasFormError(formError);
    const isValidBillingData = checkValue(billingAddressForm);
    const hasNoBillingError = hasFormError(billingAddressFormError);
    let isBillingAddressValid = false;

    if (isNotEmptyObject(billingAddressForm)) {
      isBillingAddressValid = requiredBillingFields.every((field) => {
        const value = billingAddressForm[field];
        return value !== '' && value !== null && value !== undefined;
      });
    }
    return (
      isValidFormData &&
      hasNoFormError &&
      isValidBillingData &&
      hasNoBillingError &&
      isBillingAddressValid
    );
  } else {
    return false;
  }
};

export const imageRendering = (cardType: string) => {
  const cardIcons: { [key: string]: string } = {
    Visa: PAYMENT.CREDIT_CARD_IMG.Visa,
    Mastercard: PAYMENT.CREDIT_CARD_IMG.Mastercard,
    AmericanExpress: PAYMENT.CREDIT_CARD_IMG.AmericanExpress,
    Discover: PAYMENT.CREDIT_CARD_IMG.Discover,
  };

  return cardIcons[cardType] || PAYMENT.CREDIT_CARD_IMG.Visa;
};

/**
 * Invalidates the current session using the dynamic session confirmation token.
 */
export const invalidateSession = async (
  dnSessConf: string,
  isFurniture = false,
) => {
  const sessionConfRes: any = await makeFetchCall({
    endpoint: urlMapping.invalidateSessionUrl,
    options: {
      method: 'POST',
    },
    params: {
      _dynSessConf: dnSessConf,
    },
    headers: { 'Is-Furniture': isFurniture ? 'true' : 'false' },
  });
  return sessionConfRes;
};

export function extractAddress(str: string | null) {
  return str
    ?.replace(/^.*?\b(Address|Registry Address)\s*-\s*/, '$1 - ')
    .trim();
}

export function getCartItemsForDTMEvent(commerceItems: CommerceItemInfo[]) {
  return commerceItems.length > 0
    ? commerceItems.map((item) => ({
        deliveryMethod: item?.instorePickUpOpted ? 'Store Pickup' : 'Ship Item',
        gradeRange: item?.gaEventData?.gradeRange,
        price: item?.priceInfo?.amount,
        primaryCategory: item?.gaEventData?.subcategory,
        primarySubCategory: item?.gaEventData?.category,
        productId: item?.productId,
        productName: item?.gaEventData?.productName,
        productUse: item?.gaEventData?.productUse,
        qty: item?.quantity,
        skuId: item?.catalogRefId,
        skuName: item?.skuDisplayName,
      }))
    : [];
}

export function sendOnBlurDTMEvent(data: {
  key: string;
  errorMessage: string;
  value: string;
}): void {
  const eventData = {
    event: 'formFieldBlur',
    detail: {
      fieldName: data.key,
      fieldErrorMsg: data.errorMessage,
      fieldState:
        data.errorMessage === '' && data.value !== ''
          ? 'valid'
          : data.value == ''
            ? 'empty'
            : 'error',
    },
  };
  DynamicDTMEvent(eventData);
  return;
}

export function triggerCheckoutFormErrorEvent({
  step,
  llmAPIVersion,
  isFurniture,
  errors,
  fieldName = '',
}: {
  step: string;
  llmAPIVersion: string;
  isFurniture: boolean;
  errors: FormException[];
  fieldName?: string;
}): void {
  const eventData = {
    event: 'checkoutFormError',
    detail: {
      checkoutStep: step,
      errorList: errors.map((error) => ({
        fieldErrorMessage: error.localizedMessage,
        fieldName: error.propertyPath || fieldName,
      })),
      llmAPIVersion: llmAPIVersion,
      isFurniture: isFurniture,
    },
  };
  DynamicDTMEvent(eventData);
  return;
}

/**
 * handleAPIDownErrors
 * @param apiErrors
 * @param pathName optional
 */
export const handleAPIDownErrors = (
  apiErrors: FormException[],
  pathName?: string,
): string => {
  const genericError = apiErrors.filter((el) => !el.propertyPath);
  const errorMessage = genericError?.[0]?.localizedMessage || '';
  return errorMessage;
};
